import React, { useState, useEffect } from 'react';
import { schedulerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const EmailScheduler = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [message, setMessage] = useState(null);
  const [results, setResults] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await schedulerAPI.getStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Error loading scheduler status:', error);
      setMessage({ type: 'error', text: 'Failed to load scheduler status' });
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerManual = async () => {
    if (!window.confirm('This will send device assignment emails to ALL users. This may take several minutes. Continue?')) {
      return;
    }

    try {
      setTriggering(true);
      setMessage({ type: 'info', text: 'Starting email campaign... This may take several minutes.' });
      setResults(null);

      const response = await schedulerAPI.triggerManual();
      
      setMessage({ 
        type: 'success', 
        text: `Email campaign completed! ${response.data.successCount} succeeded, ${response.data.failureCount} failed. Duration: ${response.data.durationMinutes} minutes.` 
      });
      setResults(response.data);
      
      // Reload status
      await loadStatus();
    } catch (error) {
      console.error('Error triggering email campaign:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to trigger email campaign' 
      });
    } finally {
      setTriggering(false);
    }
  };

  const handleStopScheduler = async () => {
    if (!window.confirm('Stop the automatic email scheduler?')) {
      return;
    }

    try {
      await schedulerAPI.stop();
      setMessage({ type: 'success', text: 'Scheduler stopped successfully' });
      await loadStatus();
    } catch (error) {
      console.error('Error stopping scheduler:', error);
      setMessage({ type: 'error', text: 'Failed to stop scheduler' });
    }
  };

  const handleStartScheduler = async () => {
    try {
      await schedulerAPI.start();
      setMessage({ type: 'success', text: 'Scheduler started successfully' });
      await loadStatus();
    } catch (error) {
      console.error('Error starting scheduler:', error);
      setMessage({ type: 'error', text: 'Failed to start scheduler' });
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: '700', 
          color: '#1e293b',
          marginBottom: '8px'
        }}>
          📧 Email Scheduler
        </h1>
        <p style={{ color: '#64748b', fontSize: '15px' }}>
          Automated monthly device assignment emails sent to all users
        </p>
      </div>

      {/* Message Alert */}
      {message && (
        <div style={{
          padding: '16px 20px',
          marginBottom: '24px',
          borderRadius: '8px',
          background: message.type === 'success' ? '#f0fdf4' : 
                      message.type === 'error' ? '#fef2f2' : '#eff6ff',
          border: `1px solid ${message.type === 'success' ? '#86efac' : 
                               message.type === 'error' ? '#fca5a5' : '#93c5fd'}`,
          color: message.type === 'success' ? '#166534' : 
                 message.type === 'error' ? '#991b1b' : '#1e40af'
        }}>
          {message.text}
        </div>
      )}

      {/* Status Card */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        padding: '28px',
        marginBottom: '24px'
      }}>
        <h2 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#1e293b',
          marginBottom: '20px'
        }}>
          Current Status
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '500' }}>
              Scheduler
            </div>
            <div style={{ 
              display: 'inline-block',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              background: status?.isSchedulerActive ? '#d1fae5' : '#fee2e2',
              color: status?.isSchedulerActive ? '#065f46' : '#991b1b'
            }}>
              {status?.isSchedulerActive ? '✓ Active' : '✗ Stopped'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '500' }}>
              Currently Processing
            </div>
            <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>
              {status?.isCurrentlyProcessing ? (
                <span style={{ color: '#f59e0b' }}>⏳ Yes ({status.queueLength} in queue)</span>
              ) : (
                <span style={{ color: '#10b981' }}>✓ No</span>
              )}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '500' }}>
              Email Delay
            </div>
            <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>
              {status?.emailDelay || 'N/A'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '500' }}>
              Schedule
            </div>
            <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>
              1st of every month at 9:00 AM
            </div>
          </div>
        </div>
      </div>

      {/* Actions Card */}
      {isSuperAdmin && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '28px',
          marginBottom: '24px'
        }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1e293b',
            marginBottom: '20px'
          }}>
            Actions (Super Admin Only)
          </h2>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={handleTriggerManual}
              disabled={triggering || status?.isCurrentlyProcessing}
              style={{
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: '600',
                background: triggering ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: triggering || status?.isCurrentlyProcessing ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.2s ease',
                opacity: triggering || status?.isCurrentlyProcessing ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!triggering && !status?.isCurrentlyProcessing) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
              }}
            >
              {triggering ? '⏳ Sending Emails...' : '📨 Send Emails Now'}
            </button>

            {status?.isSchedulerActive ? (
              <button
                onClick={handleStopScheduler}
                disabled={status?.isCurrentlyProcessing}
                style={{
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: '600',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: status?.isCurrentlyProcessing ? 'not-allowed' : 'pointer',
                  opacity: status?.isCurrentlyProcessing ? 0.6 : 1
                }}
              >
                ⏸️ Stop Scheduler
              </button>
            ) : (
              <button
                onClick={handleStartScheduler}
                style={{
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: '600',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                ▶️ Start Scheduler
              </button>
            )}

            <button
              onClick={loadStatus}
              style={{
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: '600',
                background: '#f3f4f6',
                color: '#1e293b',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              🔄 Refresh Status
            </button>
          </div>

          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: '#fef3c7',
            borderLeft: '4px solid #f59e0b',
            borderRadius: '4px'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
              <strong>⚠️ Important:</strong> Manual email sending will process ALL active users sequentially with 10-second delays. 
              For 50 users, this takes approximately 8-9 minutes. Do not close this page or restart the backend during the process.
            </p>
          </div>
        </div>
      )}

      {/* Results Card */}
      {results && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '28px'
        }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1e293b',
            marginBottom: '20px'
          }}>
            Last Campaign Results
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>
                {results.totalUsers}
              </div>
              <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>Total Users</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: '#f0fdf4', borderRadius: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>
                {results.successCount}
              </div>
              <div style={{ fontSize: '13px', color: '#166534', marginTop: '4px' }}>Success</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: '#fef2f2', borderRadius: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>
                {results.failureCount}
              </div>
              <div style={{ fontSize: '13px', color: '#991b1b', marginTop: '4px' }}>Failed</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: '#eff6ff', borderRadius: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>
                {results.durationMinutes?.toFixed(1)}m
              </div>
              <div style={{ fontSize: '13px', color: '#1e40af', marginTop: '4px' }}>Duration</div>
            </div>
          </div>

          {results.results && results.results.length > 0 && (
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                      Email
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                      Status
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                      Devices
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                      Error
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((result, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#1e293b' }}>
                        {result.email}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: result.success ? '#d1fae5' : '#fee2e2',
                          color: result.success ? '#065f46' : '#991b1b'
                        }}>
                          {result.success ? '✓ Sent' : '✗ Failed'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                        {result.deviceCount}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#ef4444' }}>
                        {result.error || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Info Card */}
      <div style={{
        marginTop: '24px',
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '8px',
        padding: '20px'
      }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0c4a6e', marginBottom: '12px' }}>
          ℹ️ How It Works
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#075985', fontSize: '14px', lineHeight: '1.8' }}>
          <li>Emails are automatically sent on the 1st of every month at 9:00 AM</li>
          <li>Each email includes a summary of devices currently assigned to that user</li>
          <li>Emails are sent sequentially with 10-second delays to avoid rate limiting</li>
          <li>Super admins can manually trigger email campaigns at any time</li>
          <li>Configuration can be changed in backend/config.js</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailScheduler;
